import config from "@/config/app";
import { TarotApiService } from "./tarotApi";

const PROXY_BASE = "/api/proxy";
const SHA256_PATTERN = /^[a-f0-9]{64}$/i;
const TIKTOK_EVENT_NAMES: Record<string, string> = {
  CompletePayment: "Purchase",
  SubmitForm: "Lead",
};

export type TikTokCompatibleEvent = {
  event_name: string;
  event_time?: number;
  event_source_url?: string;
  event_id?: string;
  user_data?: {
    em?: string;
    ph?: string;
    external_id?: string;
    client_ip_address?: string;
    client_user_agent?: string;
  };
  custom_data?: {
    currency?: string;
    value?: number;
    content_name?: string;
    content_category?: string;
    content_ids?: string[];
    contents?: Array<Record<string, unknown>>;
    num_items?: number;
    status?: string;
    order_id?: string;
    step?: number;
    step_label?: string;
    max_steps?: number;
    flow_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  };
};

type TikTokPixelQueue = {
  track?: (
    eventName: string,
    payload?: Record<string, unknown>,
    options?: { event_id?: string; pixel_code?: string },
  ) => void;
  identify?: (user: Record<string, string>) => void;
};

declare global {
  interface Window {
    ttq?: TikTokPixelQueue;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function getTikTokIdentifiers(): {
  ttp?: string;
  ttclid?: string;
} {
  if (typeof window === "undefined") return {};

  const url = new URL(window.location.href);
  const queryClickId = url.searchParams.get("ttclid") || undefined;
  let clickId = queryClickId || readCookie("ttclid");

  if (queryClickId) {
    try {
      document.cookie = `ttclid=${encodeURIComponent(
        queryClickId,
      )}; path=/; max-age=34190000; SameSite=Lax`;
      clickId = queryClickId;
    } catch {}
  }

  return {
    ttp: readCookie("_ttp"),
    ttclid: clickId,
  };
}

async function hashIdentifier(value?: string): Promise<string | undefined> {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (SHA256_PATTERN.test(normalized)) return normalized;
  const data = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function toTikTokPixelPayload(
  event: TikTokCompatibleEvent,
): Record<string, unknown> {
  const customData = event.custom_data || {};
  const contents = (customData.contents || []).map((item, index) => ({
    content_id:
      item.content_id ??
      item.id ??
      (customData.content_ids || [])[index],
    content_name:
      item.content_name ?? item.title ?? customData.content_name,
    content_category:
      item.content_category ?? item.category ?? customData.content_category,
    quantity: item.quantity,
    price: item.price ?? item.item_price,
  }));

  return {
    value: customData.value,
    currency: customData.currency?.toUpperCase(),
    content_type:
      contents.length > 0 || customData.content_ids?.length
        ? "product"
        : undefined,
    contents: contents.length > 0 ? contents : undefined,
    content_id:
      contents.length === 0 ? customData.content_ids?.[0] : undefined,
    content_name: customData.content_name,
    content_category: customData.content_category,
    order_id: customData.order_id,
    num_items: customData.num_items,
    step: customData.step,
    step_label: customData.step_label,
    flow_id: customData.flow_id,
  };
}

function sendTikTokPixelEvent(event: TikTokCompatibleEvent): void {
  if (!config.analytics.tiktokPixelEnabled || typeof window === "undefined") {
    return;
  }

  const queue = window.ttq;
  if (typeof queue?.track !== "function") return;

  const userData: Record<string, string> = {};
  if (event.user_data?.em && !SHA256_PATTERN.test(event.user_data.em)) {
    userData.email = event.user_data.em;
  }
  if (event.user_data?.ph && !SHA256_PATTERN.test(event.user_data.ph)) {
    userData.phone_number = event.user_data.ph;
  }
  const externalId =
    event.user_data?.external_id || event.custom_data?.flow_id;
  if (externalId && !SHA256_PATTERN.test(externalId)) {
    userData.external_id = externalId;
  }
  if (Object.keys(userData).length > 0) {
    queue.identify?.(userData);
  }

  const eventName =
    TIKTOK_EVENT_NAMES[event.event_name] || event.event_name;
  const pixelId = config.analytics.tiktokPixelId.trim() || undefined;
  queue.track(eventName, toTikTokPixelPayload(event), {
    event_id: event.event_id,
    pixel_code: pixelId,
  });
}

export async function sendTikTokEvent(
  event: TikTokCompatibleEvent,
): Promise<unknown> {
  if (typeof window === "undefined") return;

  // The browser channel and server channel use the same event_id.
  sendTikTokPixelEvent(event);

  if (!config.analytics.tiktokEventsApiEnabled) return;

  const identifiers = getTikTokIdentifiers();
  const enriched: TikTokCompatibleEvent & {
    user_data: NonNullable<TikTokCompatibleEvent["user_data"]> & {
      ttp?: string;
      ttclid?: string;
    };
  } = {
    ...event,
    event_time: event.event_time || Math.floor(Date.now() / 1000),
    event_source_url: event.event_source_url || window.location.href,
    user_data: {
      ...event.user_data,
      em: await hashIdentifier(event.user_data?.em),
      ph: await hashIdentifier(event.user_data?.ph),
      // flow_id is a first-party, pseudonymous identifier available from the
      // first PageView, so TikTok can match anonymous visits consistently.
      external_id: await hashIdentifier(
        event.user_data?.external_id || event.custom_data?.flow_id,
      ),
      client_user_agent:
        event.user_data?.client_user_agent || navigator.userAgent,
      ttp: identifiers.ttp,
      ttclid: identifiers.ttclid,
    },
  };

  const language = config.i18n.language || "pt-BR";
  const endpoint = `${PROXY_BASE}/analytics/tiktok/events?language=${encodeURIComponent(
    language,
  )}`;
  const doPost = () =>
    fetch(endpoint, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: [enriched] }),
    });

  let response = await doPost();
  if (response.status === 401 || response.status === 403) {
    try {
      await TarotApiService.autoLogin();
      response = await doPost();
    } catch {}
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    return {
      ok: false,
      status: response.status,
      detail: error.detail || `HTTP ${response.status}`,
    };
  }
  return response.json();
}

export async function sendTikTokEventSafe(
  event: TikTokCompatibleEvent,
): Promise<unknown> {
  try {
    return await sendTikTokEvent(event);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[TikTok] Event send failed (best-effort)", {
        eventName: event.event_name,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
